import React, { useEffect, useState } from 'react';
import videoIcon from 'assets/images/video-thumb.svg';
import { parseCropHash, getFramedImageStyles } from 'utils/cropUtils';
import ImageAdjustModal from './ImageAdjustModal';
import {
  CloseImageButton,
  LocalErrorMessage,
  Input,
  InputLabel,
  UploadImageContainer,
  UploadImageHeader,
  UploadImageList,
  UploadImageListItem,
  UploadImageTitle,
} from './styles';

interface IImage {
  id: number;
  name: string;
  url: string;
}

export interface UploadImageProps {
  descriptionText?: string;
  multipleFiles: boolean;
  importedFiles?: IImage[];
  limitFiles?: number;
  htmlFor?: string;
  onChange?: (files: Array<IImage | File>) => void;
  required?: boolean;
  aspectRatio?: number;
  accept?: string;
}

export const UploadImage = ({
  required,
  descriptionText,
  multipleFiles,
  limitFiles,
  importedFiles,
  htmlFor,
  aspectRatio,
  accept = 'image/*',
  onChange,
}: UploadImageProps): JSX.Element => {
  const [uploadedFiles, setUploadedFiles] = useState<File[] | null>(null);
  const newFiles = importedFiles;
  const [error, setError] = useState<string | null>(null);
  const [isFileViewerVisible, setIsFileViewerVisible] = useState(false);
  const [isImageAdjustVisible, setIsImageAdjustVisible] = useState(false);
  const [fileToAdjust, setFileToAdjust] = useState<File>();
  const newFilesNumber = newFiles?.length ?? 0;
  const uploadedFilesNumber = uploadedFiles?.length ?? 0;
  const [inputLabelDisabled, setInputLabelDisabled] = useState(false);

  useEffect(() => {
    if (limitFiles && newFilesNumber + uploadedFilesNumber >= limitFiles) {
      setInputLabelDisabled(true);
    } else {
      setInputLabelDisabled(false);
    }
  }, [newFilesNumber, uploadedFilesNumber, limitFiles]);

  const errorReturn = () => {
    setError(`* Você pode adicionar apenas ${limitFiles} arquivos`);
    !newFiles ? setUploadedFiles([]) : null;
    setIsFileViewerVisible(false);
  };

  const errorPDFFile = () => {
    setError('* Não é permitido adicionar arquivos PDF');
    !newFiles ? setUploadedFiles([]) : null;
    setIsFileViewerVisible(false);
  };

  const errorRequired = () => {
    setError('* Campo obrigatório');
    !newFiles ? setUploadedFiles([]) : null;
    setIsFileViewerVisible(false);
  };

  const errorNonImageFile = () => {
    setError('* Apenas imagens são permitidas');
    !newFiles ? setUploadedFiles([]) : null;
    setIsFileViewerVisible(false);
  };

  const handleChange = (images = newFiles, files = uploadedFiles) => {
    if (!onChange) {
      return;
    }

    onChange([...(images ?? []), ...(files ?? [])]);
  };

  const onImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventFilesNumber = event.target.files?.length ?? 0;
    const totalFiles = eventFilesNumber + uploadedFilesNumber + newFilesNumber;

    switch (true) {
      case eventFilesNumber > (limitFiles ?? 0):
        errorReturn();
        break;

      case totalFiles > (limitFiles ?? 0):
        errorReturn();
        break;

      case Array.from(event.target.files ?? []).some(
        file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
      ):
        errorPDFFile();
        break;

      case required && totalFiles === 0:
        errorRequired();
        break;

      case accept === 'image/*' && Array.from(event.target.files ?? []).some(file => !file.type.startsWith('image/')):
        errorNonImageFile();
        break;
      default:
        setError(null);
        const current = uploadedFiles ?? [];
        const fromTarget = Array.from(event.target.files ?? []);
        const newUploadedFiles = [...current, ...fromTarget];
        setUploadedFiles(newUploadedFiles);
        handleChange(newFiles, newUploadedFiles);
        break;
    }
  };

  const removeImage = (file: File) => {
    setError(null);
    setUploadedFiles(uploadedFiles => (uploadedFiles ? uploadedFiles.filter(f => f !== file) : null));
    setIsFileViewerVisible(false);
    handleChange();
  };

  const removeImportedImage = (id: number) => {
    setError(null);
    handleChange(newFiles?.filter(file => file.id !== id));
    setIsFileViewerVisible(false);
  };

  const getFileThumb = (file: IImage | File): string => {
    if ('url' in file) {
      return file.url.endsWith('.mp4') ? videoIcon : file.url;
    }

    const isVideo = file.type.startsWith('video/');
    if (isVideo) return videoIcon;
    const blobUrl = URL.createObjectURL(file);
    const cropHash = (file as any).cropHash;
    return cropHash ? `${blobUrl}#${cropHash}` : blobUrl;
  };

  useEffect(() => {
    if (!newFiles && !uploadedFiles) {
      handleChange();
    }
  }, []);

  return (
    <UploadImageContainer>
      <UploadImageHeader>
        <UploadImageTitle>
          {descriptionText} {required && <span>*</span>}
        </UploadImageTitle>
        <InputLabel
          htmlFor={htmlFor}
          onClick={() => {
            setError(null);
            setIsFileViewerVisible(true);
          }}
          className={inputLabelDisabled ? 'disabledButton' : ''}>
          IMPORTAR IMAGEM
        </InputLabel>
        {isFileViewerVisible && (
          <Input
            hidden
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const file = event.target.files?.item(0);
              const isImage = file && file?.type.startsWith('image/');
              const hasAspectRatio = aspectRatio !== undefined;
              const hasOneFile = event.target.files?.length === 1;

              if (isImage && hasAspectRatio && hasOneFile) {
                setIsImageAdjustVisible(true);
                setFileToAdjust(file);
                return;
              }

              onImport(event);
            }}
            accept={accept}
            id={htmlFor}
            type="file"
            multiple={multipleFiles}
            disabled={inputLabelDisabled}
          />
        )}
      </UploadImageHeader>

      <UploadImageList>
        {newFiles?.map((file: IImage) => {
          const thumbUrl = getFileThumb(file);
          const { cleanUrl, crop } = parseCropHash(thumbUrl);
          const { containerStyle, imageStyle } = getFramedImageStyles(crop);
          return (
            <UploadImageListItem
              style={{
                position: 'relative',
                overflow: 'hidden',
                background: `#ccc url(${cleanUrl}) center center / cover no-repeat`,
              }}
              key={file.id}>
              {crop && (
                <div style={containerStyle}>
                  <img src={cleanUrl} alt="" style={imageStyle} />
                </div>
              )}
              <CloseImageButton type="button" onClick={() => removeImportedImage(file.id)} style={{ zIndex: 10 }}>
                X
              </CloseImageButton>
            </UploadImageListItem>
          );
        })}
        {uploadedFiles?.map((file: File, index) => {
          const thumbUrl = getFileThumb(file);
          const { cleanUrl, crop } = parseCropHash(thumbUrl);
          const { containerStyle, imageStyle } = getFramedImageStyles(crop);
          return (
            <UploadImageListItem
              style={{
                position: 'relative',
                overflow: 'hidden',
                background: `#ccc url(${cleanUrl}) center center / cover no-repeat`,
              }}
              key={index}>
              {crop && (
                <div style={containerStyle}>
                  <img src={cleanUrl} alt="" style={imageStyle} />
                </div>
              )}
              <CloseImageButton type="button" onClick={() => removeImage(file)} style={{ zIndex: 10 }}>
                X
              </CloseImageButton>
            </UploadImageListItem>
          );
        })}
        {(newFiles?.length ?? 0) === 0 && (uploadedFiles?.length ?? 0) === 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '10px 0',
              width: '100%',
              height: '50px',
              color: '#aaa',
              background: '#f1f1f1',
              borderRadius: '18px',
            }}>
            Nenhum arquivo importado
          </div>
        )}
      </UploadImageList>

      {error && <LocalErrorMessage>{error}</LocalErrorMessage>}
      {aspectRatio && (
        <ImageAdjustModal
          open={isImageAdjustVisible}
          file={fileToAdjust}
          aspectRatio={aspectRatio}
          onCancel={() => {
            setIsImageAdjustVisible(false);
            setFileToAdjust(undefined);
          }}
          onConfirm={file => {
            setIsImageAdjustVisible(false);
            setFileToAdjust(undefined);
            const fileEvent = { target: { files: [file] } };
            onImport(fileEvent as any);
          }}
        />
      )}
    </UploadImageContainer>
  );
};

export default UploadImage;
